import React from 'react';
import { Alert } from 'reactstrap';

const normalizeType = (color) => {
  if (!color) return 'information';

  const value = String(color).toLowerCase().trim();

  if ([
    'info',
    'information',
    'informational',
    'primary',
  ].includes(value)) {
    return 'information';
  }

  if ([
    'success',
    'ok',
    'done',
    'positive',
  ].includes(value)) {
    return 'success';
  }

  if ([
    'warn',
    'warning',
    'caution',
    'alert',
  ].includes(value)) {
    return 'warning';
  }

  if ([
    'error',
    'danger',
    'fail',
    'failed',
    'critical',
  ].includes(value)) {
    return 'error';
  }

  // Fallback
  return 'information';
};

const getVariantConfig = (type) => {
  switch (type) {
    case 'success':
      return {
        title: 'Success',
        icon: '✓',
        // Light, fully opaque green gradient (no transparency)
        glassBackground:
          'linear-gradient(135deg, rgb(209, 250, 229), rgb(134, 239, 172))',
        glassBorder: 'rgba(74, 222, 128, 0.45)',
        accent: 'rgba(22, 163, 74, 1)',
        accentSoft: 'rgba(34, 197, 94, 0.16)',
      };
    case 'warning':
      return {
        title: 'Warning',
        icon: '!',
        // Light, fully opaque amber gradient (no transparency)
        glassBackground:
          'linear-gradient(135deg, rgb(254, 243, 199), rgb(253, 230, 138))',
        glassBorder: 'rgba(251, 191, 36, 0.45)',
        accent: 'rgba(217, 119, 6, 1)',
        accentSoft: 'rgba(245, 158, 11, 0.16)',
      };
    case 'error':
      return {
        title: 'Error',
        icon: '⨯',
        // Light, fully opaque red gradient (no transparency)
        glassBackground:
          'linear-gradient(135deg, rgb(254, 226, 226), rgb(252, 165, 165))',
        glassBorder: 'rgba(252, 165, 165, 0.45)',
        accent: 'rgba(220, 38, 38, 1)',
        accentSoft: 'rgba(248, 113, 113, 0.16)',
      };
    case 'information':
    default:
      return {
        title: 'Information',
        icon: 'i',
        // Light, fully opaque blue gradient (no transparency)
        glassBackground:
          'linear-gradient(135deg, rgb(219, 234, 254), rgb(147, 197, 253))',
        glassBorder: 'rgba(96, 165, 250, 0.45)',
        accent: 'rgba(37, 99, 235, 1)',
        accentSoft: 'rgba(59, 130, 246, 0.16)',
      };
  }
};

const CustomNotifications = ({ message, hide, color }) => {
  const type = normalizeType(color);
  const variant = getVariantConfig(type);

  const glassStyles = {
    position: 'fixed',
    top: '12px',
    right: '20px',
    zIndex: 9999,
    // Subtle colorful gradient per variant, no strong glass/blur
    background: variant.glassBackground,
    backgroundColor: 'transparent',
    // No border or heavy glass effect to keep it clean and minimal
    borderRadius: '16px',
    // Single soft drop shadow (removed 1px outline that looked like a gray border)
    boxShadow: '0 18px 45px rgba(15, 23, 42, 0.25)',
    padding: '10px 14px 10px 10px',
    minWidth: '280px',
    maxWidth: '420px',
    color: '#0f172a',
  };

  const titleStyle = {
    fontSize: '0.88rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    marginBottom: message?.message ? '2px' : 0,
    color: variant.accent,
  };

  const messageStyle = {
    fontSize: '0.84rem',
    fontWeight: 400,
    lineHeight: 1.45,
    color: '#0f172a',
  };

  const iconContainerStyle = {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '999px',
    background: variant.accentSoft,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: variant.accent,
    fontSize: '1.05rem',
    fontWeight: 600,
  };

  const closeIconStyle = {
    fontSize: '18px',
    cursor: 'pointer',
    marginLeft: '12px',
    opacity: 0.7,
  };

  const contentWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
  };

  const textBlockWrapperStyle = {
    marginLeft: '10px',
    display: 'flex',
    flexDirection: 'column',
  };

  const isStringMessage = typeof message === 'string';
  const bodyText = isStringMessage ? message : message?.message;
  const titleText = (!isStringMessage && message?.title) ? message.title : variant.title;

  return (
    <div
      className="alert-time"
      style={{
        background: 'transparent',
        backgroundColor: 'transparent',
        boxShadow: 'none',
      }}
    >
      <Alert
        className="d-flex justify-content-between align-items-center"
        fade={false}
        style={glassStyles}
      >
        <div style={contentWrapperStyle}>
          <div style={iconContainerStyle}>
            <span>{variant.icon}</span>
          </div>
          <div style={textBlockWrapperStyle}>
            <div style={titleStyle}>{titleText}</div>
            {bodyText && <div style={messageStyle}>{bodyText}</div>}
          </div>
        </div>
        <div
          className="icon-close"
          onClick={hide}
          style={closeIconStyle}
        ></div>
      </Alert>
    </div>
  );
};

export default CustomNotifications;
